import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { APP_ROUTES, generatePlanningRoute } from 'app/common/constants';
import { InspectionsService } from 'app/core/services/inspections.service';
import { firstValueFromRx6 } from 'app/core/utils/first-value-from-rx6';

type ReportType = 'full' | 'summary';
type PageStatus = 'loading' | 'error' | 'closing' | 'complete';

@Component({
  selector: 'app-inspection-report-download',
  templateUrl: './inspection-report-download.component.html',
  styleUrls: ['./inspection-report-download.component.scss'],
})
export class InspectionReportDownloadComponent implements OnInit {
  status: PageStatus = 'loading';
  errorMessage: string | null = null;
  inspectionDetailUrl: string | null = null;

  private started = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly inspectionsService: InspectionsService
  ) {}

  ngOnInit(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    void this.runDownload();
  }

  private async runDownload(): Promise<void> {
    const inspectionId = this.route.snapshot.paramMap.get('id');
    if (!inspectionId) {
      this.setError('Invalid inspection id.');
      return;
    }

    this.inspectionDetailUrl = generatePlanningRoute(
      APP_ROUTES.PLANNING.INSPECTION_DETAIL.replace(':id', inspectionId)
    );

    const typeParam = this.route.snapshot.queryParamMap.get('type');
    const reportType: ReportType = typeParam === 'summary' ? 'summary' : 'full';

    try {
      const response = await firstValueFromRx6(
        this.inspectionsService.getInspectionReportPdf(inspectionId, reportType)
      );
      const sasUrl = reportType === 'full' ? response?.reportUrl : response?.summaryReportUrl;
      const trimmed = typeof sasUrl === 'string' ? sasUrl.trim() : '';
      if (!trimmed) {
        this.setError(
          reportType === 'summary'
            ? 'No summary report is available for this inspection.'
            : 'No inspection report is available for this inspection.'
        );
        return;
      }

      const filename = `inspection-${inspectionId}-${reportType}.pdf`;
      await this.downloadFromSasUrl(trimmed, filename);
      await this.attemptCloseTabAfterDownload();
    } catch (err) {
      console.error(err);
      this.setError('Failed to download the inspection report.');
    }
  }

  private setError(message: string): void {
    this.errorMessage = message;
    this.status = 'error';
  }

  private async downloadFromSasUrl(sasUrl: string, filename: string): Promise<void> {
    const response = await fetch(sasUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch report (${response.status})`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  private async attemptCloseTabAfterDownload(): Promise<void> {
    await this.delay(400);
    this.status = 'closing';
    window.close();
    await this.delay(1500);
    window.close();
    this.status = 'complete';
  }

  get statusMessage(): string {
    if (this.status === 'closing') {
      return 'Download started. Closing…';
    }
    if (this.status === 'complete') {
      return 'Download complete. You may close this tab.';
    }
    if (this.status === 'error') {
      return this.errorMessage ?? 'Unable to download report.';
    }
    return 'Preparing your download…';
  }
}
